import { computed } from 'vue';
import { usePersisted } from './use_persisted.js';
import { getServerUrl } from './server_url.js';

// A bearer token cached in localStorage rather than a cookie session — this
// app needs to know "am I logged in" without a network round trip (so it
// keeps working offline once a device has logged in at least once), which a
// token it can read synchronously satisfies directly. Login/register are
// entirely optional: every feature in the app already works with nothing
// here ever set, purely off localStorage — this module only ever adds
// sync on top, never gates anything.
const token = usePersisted('auth-token', null);
const email = usePersisted('auth-email', null);
// Cached alongside the token so the scanner page can gate itself without a
// network round trip on every render — refreshed by requestToken/
// updateProfile, and by refreshProfile() below for the case where
// score_sheet_scanner got granted server-side (a direct DB edit, see
// server/db.js) after this browser already logged in.
const scannerAccess = usePersisted('auth-scanner-access', false);

const isLoggedIn = computed(() => !!token.value);

async function requestToken(path, emailInput, password) {
  const res = await fetch(`${getServerUrl()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: emailInput, password }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Request failed.');

  token.value = body.token;
  email.value = body.email;
  scannerAccess.value = !!body.scanner_access;
}

function register(emailInput, password) {
  return requestToken('/register', emailInput, password);
}

function login(emailInput, password) {
  return requestToken('/login', emailInput, password);
}

// Local-only — clears the cached token/email but never touches server data
// or any locally-stored setup. Logging out just stops syncing.
function logout() {
  token.value = null;
  email.value = null;
  scannerAccess.value = false;
}

function authHeader() {
  return token.value ? { Authorization: `Bearer ${token.value}` } : {};
}

// currentPassword is always required server-side (see server/index.js's
// updateProfile) even though this call is already authenticated — changing
// the login credentials on a bearer token alone would mean a leaked/stale
// token is enough to lock the real owner out permanently.
async function updateProfile({ currentPassword, newEmail, newPassword }) {
  const res = await fetch(`${getServerUrl()}/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ currentPassword, newEmail, newPassword }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Profile update failed.');

  token.value = body.token;
  email.value = body.email;
  scannerAccess.value = !!body.scanner_access;
}

// Same currentPassword requirement as updateProfile, for the same reason —
// this is the most destructive action in the app (permanently deletes the
// account and every setup synced to it), so a bare bearer token alone
// shouldn't be enough to trigger it. Only clears the local token/email/
// scannerAccess on success (same as logout()) — clearing the locally
// cached setups too is the caller's job (see account_widget.vue and
// profile.vue's onLogout/deleteAccount, which also call
// useSetups().replaceAllSetups and useSync().resetSyncState), since this
// module has no reason to know about setups.js/sync.js.
async function deleteAccount(currentPassword) {
  const res = await fetch(`${getServerUrl()}/account`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify({ currentPassword }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'Account deletion failed.');

  logout();
}

// Best-effort refresh of email/scannerAccess for the current token — call
// this before gating on scannerAccess (e.g. on the scanner page mount) so a
// permission grant made after this browser last logged in takes effect
// without forcing a fresh login. Silently does nothing if logged out or the
// request fails (offline, expired token, etc.) — callers just keep using
// whatever was already cached.
async function refreshProfile() {
  if (!token.value) return;
  try {
    const res = await fetch(`${getServerUrl()}/me`, { headers: authHeader() });
    if (!res.ok) return;
    const body = await res.json();
    email.value = body.email;
    scannerAccess.value = !!body.scanner_access;
  } catch {
    // offline or unreachable — keep the cached values as-is
  }
}

export function useAuth() {
  return { isLoggedIn, email, scannerAccess, register, login, logout, authHeader, updateProfile, deleteAccount, refreshProfile };
}
